import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { startOfDay, endOfDay, startOfWeek, startOfMonth } from 'date-fns';
import type { DatePreset, DateRange } from '@/types';

interface DateRangeState extends DateRange {
  setPreset: (preset: DatePreset) => void;
  setCustomRange: (startDate: Date, endDate: Date) => void;
}

function getDatesByPreset(preset: DatePreset): { startDate: Date; endDate: Date } {
  const now = new Date();

  switch (preset) {
    case 'today':
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
      };
    case 'this_week':
      return {
        startDate: startOfWeek(now, { weekStartsOn: 0 }),
        endDate: endOfDay(now),
      };
    case 'this_month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfDay(now),
      };
    case 'custom':
    default:
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
      };
  }
}

export const useDateRangeStore = create<DateRangeState>()(
  persist(
    (set) => ({
      preset: 'this_week' as DatePreset,
      ...getDatesByPreset('this_week'),

      setPreset: (preset) => {
        const dates = getDatesByPreset(preset);
        set({ preset, ...dates });
      },

      setCustomRange: (startDate, endDate) =>
        set({
          preset: 'custom',
          startDate: startOfDay(startDate),
          endDate: endOfDay(endDate),
        }),
    }),
    {
      name: 'date-range-storage',
      partialize: (state) => ({
        preset: state.preset,
        startDate: state.startDate.toISOString(),
        endDate: state.endDate.toISOString(),
      }),
      merge: (persisted: any, current) => ({
        ...current,
        preset: persisted?.preset || 'this_week',
        startDate: persisted?.startDate ? new Date(persisted.startDate) : current.startDate,
        endDate: persisted?.endDate ? new Date(persisted.endDate) : current.endDate,
      }),
    }
  )
);
